import {DatePicker} from '@ark-ui/react/date-picker';
import {faCalendar} from '@fortawesome/pro-light-svg-icons/faCalendar';
import {faChevronLeft} from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import {faChevronRight} from '@fortawesome/pro-light-svg-icons/faChevronRight';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import type {DateValue} from '@internationalized/date';
import {getDateFormatString} from '@utils';
import {isMatch, parse} from 'date-fns';
import type {ChangeEvent, FocusEventHandler, KeyboardEventHandler, Ref} from 'react';
import {useEffect, useMemo, useState} from 'react';

import {useTableFiltersStyleConfig} from '../../theme-config';
import {IconButton} from '../IconButton';

import {calendarDateFormatter, formatDateToISO} from './helpers';

import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  forwardRef,
} from '@chakra-ui/react';

type InlineDatePickerProps = {date: string; setDate: (value: string) => void; onKeyDown: KeyboardEventHandler};

const InlineDatePicker = forwardRef((props: InlineDatePickerProps, ref: Ref<HTMLInputElement>) => {
  const {date, setDate, onKeyDown} = props;
  const todayISO = formatDateToISO(new Date());
  const dateValue = date.trim() ? [date] : [todayISO];
  // TODO should we make these more generic?
  const inputCss = useTableFiltersStyleConfig(`searchInput`);

  const [open, setOpen] = useState(false);

  const focusHandler: FocusEventHandler<HTMLDivElement> = event => {
    if (event.target.getAttribute(`data-part`) === `input` && open) {
      setOpen(false);
    }
  };

  const calendarClickHandler = () => setOpen(!open);

  const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    const format = getDateFormatString();
    if (isMatch(value, format)) {
      const parsedDate = parse(value, format, new Date());
      const formattedISODate = formatDateToISO(parsedDate);
      setDate(formattedISODate);
    }
  };

  const valueChangeHandler = ({value}: {value: Array<DateValue>}) => {
    const [date] = value[0].toString().split(`T`);
    setDate(date);
  };

  // initiate default value on first render
  // this solve an issue when user apply default date without changing calendar
  const firstDate = dateValue[0];
  useEffect(() => {
    if (!date) {
      setDate(firstDate);
    }
  }, [date, firstDate, setDate]);

  return (
    <DatePicker.Root
      closeOnSelect={false}
      open={open}
      format={calendarDateFormatter}
      value={dateValue}
      onValueChange={valueChangeHandler}
      onFocus={focusHandler}
      onChange={changeHandler}
      onKeyDown={onKeyDown}>
      <DatePicker.Control asChild>
        <InputGroup size={`sm`}>
          <DatePicker.Input asChild>
            <Input size={`sm`} sx={inputCss} ref={ref} placeholder={getDateFormatString().toLowerCase()} />
          </DatePicker.Input>

          <DatePicker.Trigger asChild>
            <InputRightElement>
              <IconButton
                aria-label={`calendar`}
                icon={<FontAwesomeIcon icon={faCalendar} />}
                size={`xs`}
                onClick={calendarClickHandler}
              />
            </InputRightElement>
          </DatePicker.Trigger>
        </InputGroup>
      </DatePicker.Control>
      <DatePicker.View view={`day`}>
        <DatePicker.Context>
          {api =>
            open ? (
              <Box maxWidth={`224px`}>
                <DatePicker.ViewControl asChild>
                  <Flex
                    justifyContent={`space-between`}
                    alignItems={`center`}
                    minHeight={`64px`}
                    borderBottomStyle={`solid`}
                    borderBottomWidth={`1px`}
                    borderBottomColor={`gray.200`}>
                    <Box
                      sx={{
                        fontSize: `xl`,
                        fontWeight: `semibold`,
                        display: `flex`,
                        alignItems: `center`,
                        cursor: `default`,
                      }}>
                      <DatePicker.RangeText />
                    </Box>
                    <Flex>
                      <Arrow direction={`left`} label={`Previous month`} />

                      <Arrow direction={`right`} label={`Next month`} />
                    </Flex>
                  </Flex>
                </DatePicker.ViewControl>

                <DatePicker.Table asChild>
                  <Table sx={{position: `relative`}}>
                    <DatePicker.TableHead asChild>
                      <Thead>
                        <DatePicker.TableRow asChild>
                          <Tr>
                            {api.weekDays.map((weekDay, id) => (
                              <DatePicker.TableHeader key={id} asChild>
                                <Th sx={{textAlign: `center`, border: 0, fontWeight: `normal`, color: `td`}}>
                                  {weekDay.short.substring(0, 2)}
                                </Th>
                              </DatePicker.TableHeader>
                            ))}
                          </Tr>
                        </DatePicker.TableRow>
                      </Thead>
                    </DatePicker.TableHead>

                    <DatePicker.TableBody asChild>
                      <Tbody>
                        {api.weeks.map((week, id) => (
                          <DatePicker.TableRow key={id} asChild>
                            <Tr>
                              {week.map((day, id) => {
                                const disabled =
                                  api.visibleRange.start.compare(day) > 0 || api.visibleRange.end.compare(day) < 0;

                                const current = api.value?.[0] ? api.value[0].compare(day) === 0 : false;
                                return (
                                  <DatePicker.TableCell key={id} value={day} asChild>
                                    <Td sx={{textAlign: `center`, border: 0, padding: `2`}}>
                                      <DatePicker.TableCellTrigger style={{width: `100%`}} asChild>
                                        <Text
                                          sx={{
                                            paddingY: `4`,
                                            color: disabled ? `blackAlpha.400` : undefined,
                                            fontSize: `sm`,
                                            letterSpacing: `-1px`,
                                            backgroundColor: current ? `blackAlpha.300` : undefined,
                                            borderRadius: `50%`,
                                            cursor: disabled ? `default` : `pointer`,
                                            _hover:
                                              current || disabled
                                                ? undefined
                                                : {
                                                    backgroundColor: `#076EFF`,
                                                    color: `white`,
                                                  },
                                          }}>
                                          {day.day}
                                        </Text>
                                      </DatePicker.TableCellTrigger>
                                    </Td>
                                  </DatePicker.TableCell>
                                );
                              })}
                            </Tr>
                          </DatePicker.TableRow>
                        ))}
                      </Tbody>
                    </DatePicker.TableBody>
                  </Table>
                </DatePicker.Table>
              </Box>
            ) : (
              <></>
            )
          }
        </DatePicker.Context>
      </DatePicker.View>
    </DatePicker.Root>
  );
});

export default InlineDatePicker;

type ArrowProps = {
  direction: 'left' | 'right';
  label: string;
};
const Arrow = ({direction, label}: ArrowProps) => {
  const icon = direction === `left` ? faChevronLeft : faChevronRight;
  const button = useMemo(
    () => (
      <IconButton
        aria-label={label}
        variant={`unstyled`}
        icon={<FontAwesomeIcon icon={icon} />}
        sx={{
          margin: `2`,
          padding: `4`,
          height: `auto`,
          minHeight: `16`,
          width: `auto`,
          minWidth: `16`,
          fontSize: `sm`,
          _hover: {
            backgroundColor: `blackAlpha.100`,
          },
        }}
      />
    ),
    [icon, label],
  );

  return direction === `left` ? (
    <DatePicker.PrevTrigger asChild>{button}</DatePicker.PrevTrigger>
  ) : (
    <DatePicker.NextTrigger asChild>{button}</DatePicker.NextTrigger>
  );
};
